/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// Promise version of blob to base64
import { isIE, isEdge } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { imgBufferToBlob } from "dc-preview-utils/src/Utils/UrlUtils";

export const blobToBase64 = blob =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });

export const imgBufferToBase64 = (buffer, mimeType) => {
  const blob = imgBufferToBlob(buffer, mimeType);
  return blobToBase64(blob);
};

const getDigest = arrayBuffer => {
  if (isIE()) {
    return window.msCrypto.subtle.digest("SHA-256", arrayBuffer);
  } else if (window.crypto && window.crypto.subtle) {
    return window.crypto.subtle.digest("SHA-256", arrayBuffer);
  }

  return Promise.reject("Crypto is not supported");
};

const getDigestHex = buffer => {
  const byteArray = new Uint8Array(buffer);

  const hexCodes = [...byteArray].map(value => {
    const hexCode = value.toString(16);
    const paddedHexCode = hexCode.padStart(2, "0");
    return paddedHexCode;
  });

  return hexCodes.join("");
};

export const getSHA256Digest = buffer =>
  new Promise((resolve, reject) => {
    if (isIE()) {
      const arrayBuffer = stringToUint8ArrayBuffer(buffer);
      let data = "";
      let operation = getDigest(arrayBuffer);
      operation.process(arrayBuffer);
      operation.finish();
      operation.oncomplete = function() {
        data = operation.result;
        if (data !== null) {
          let documentHex = getDigestHex(data);
          resolve(documentHex);
        } else {
          reject("documentID hash error");
        }
      };
    } else {
      const arrayBuffer = isEdge() ? stringToUint8ArrayBuffer(buffer) : new TextEncoder().encode(buffer);
      getDigest(arrayBuffer)
        .then(digestBuffer => {
          let documentHex = getDigestHex(digestBuffer);
          resolve(documentHex);
        })
        .catch(err => {
          reject(err);
        });
    }
  });

const stringToUint8ArrayBuffer = str => {
  var Len = 0;
  if (str) {
    Len = str.length;
    var resPos = -1;
    var resArr = typeof Uint8Array === "undefined" ? new Array(Len * 1.5) : new Uint8Array(Len * 3);
    for (var point = 0, nextcode = 0, i = 0; i !== Len; ) {
      point = str.charCodeAt(i);
      i += 1;
      if (point >= 0xd800 && point <= 0xdbff) {
        if (i === Len) {
          resArr[(resPos += 1)] = 0xef /* 0b11101111*/;
          resArr[(resPos += 1)] = 0xbf /* 0b10111111*/;
          resArr[(resPos += 1)] = 0xbd /* 0b10111101*/;
          break;
        }
        nextcode = str.charCodeAt(i);
        if (nextcode >= 0xdc00 && nextcode <= 0xdfff) {
          point = (point - 0xd800) * 0x400 + nextcode - 0xdc00 + 0x10000;
          i += 1;
          if (point > 0xffff) {
            resArr[(resPos += 1)] = (0x1e /* 0b11110*/ << 3) | (point >>> 18);
            resArr[(resPos += 1)] = (0x2 /* 0b10*/ << 6) | ((point >>> 12) & 0x3f) /* 0b00111111*/;
            resArr[(resPos += 1)] = (0x2 /* 0b10*/ << 6) | ((point >>> 6) & 0x3f) /* 0b00111111*/;
            resArr[(resPos += 1)] = (0x2 /* 0b10*/ << 6) | (point & 0x3f) /* 0b00111111*/;
          }
        } else {
          resArr[(resPos += 1)] = 0xef /* 0b11101111*/;
          resArr[(resPos += 1)] = 0xbf /* 0b10111111*/;
          resArr[(resPos += 1)] = 0xbd /* 0b10111101*/;
        }
      }
      if (point <= 0x007f) {
        resArr[(resPos += 1)] = (0x0 /* 0b0*/ << 7) | point;
      } else if (point <= 0x07ff) {
        resArr[(resPos += 1)] = (0x6 /* 0b110*/ << 5) | (point >>> 6);
        resArr[(resPos += 1)] = (0x2 /* 0b10*/ << 6) | (point & 0x3f) /* 0b00111111*/;
      } else {
        resArr[(resPos += 1)] = (0xe /* 0b1110*/ << 4) | (point >>> 12);
        resArr[(resPos += 1)] = (0x2 /* 0b10*/ << 6) | ((point >>> 6) & 0x3f) /* 0b00111111*/;
        resArr[(resPos += 1)] = (0x2 /* 0b10*/ << 6) | (point & 0x3f) /* 0b00111111*/;
      }
    }
    if (typeof Uint8Array !== "undefined") {
      return resArr.subarray(0, resPos + 1);
    }
    // else // IE 6-9
    resArr.length = resPos + 1; // trim off extra weight
    return resArr;
  }
  return "";
};
